/*
 * MADE X — native color swatches (replaces the GLO Color Swatch app)
 * -----------------------------------------------------------------
 * Works together with (search the theme for "MX-SWATCH"):
 *   snippets/product-media-gallery-content.liquid  product page shows only the selected color's photos (server-rendered, no flash)
 *   snippets/variant-main-picker.liquid            "Color: X" / "Size: X" labels, swatch tooltips, variant data used below
 *   snippets/variant-swatches.liquid               card swatches wrap (no "+N"), color preselected, link to an in-stock size
 *   snippets/card-gallery.liquid                   card photos grouped by color
 *   assets/mx-swatches.css                         swatch + size button look
 *   layout/theme.liquid                            loads this file + the CSS
 *
 * What this script does:
 *   1. Product cards: hovering or tapping a color swatch shows only that color's photos
 *      (main image, hover image and carousel), and returns to the selected color afterwards.
 *   2. Product page: when a shopper switches color and their current size is sold out in the
 *      new color, it switches to the closest in-stock size instead of showing "Sold out".
 */
(() => {
  if (window.mxSwatches) return;

  const CARD_PICKER = 'swatches-variant-picker-component';

  /* ---------------- Product cards ---------------- */

  /**
   * Shows one color's photo group on a card and hides the others.
   * @param {Element | null} card
   * @param {string | null} groupId - media id of the color's first photo
   */
  function showCardGroup(card, groupId) {
    if (!card || !groupId) return;

    const slides = card.querySelectorAll('slideshow-slide[data-mx-group]');
    if (!slides.length) return;
    if (![...slides].some((slide) => slide.getAttribute('data-mx-group') === groupId)) return;

    slides.forEach((slide) => {
      const group = slide.getAttribute('data-mx-group');
      const show = group === groupId || group === 'shared';
      if (slide.hidden === show) slide.hidden = !show;
    });
  }

  /** @param {Element} card */
  function selectedCardGroup(card) {
    const checked = card.querySelector(`${CARD_PICKER} input[type="radio"]:checked[data-option-media-id]`);
    if (checked instanceof HTMLElement && checked.dataset.optionMediaId) return checked.dataset.optionMediaId;

    const gallery = card.querySelector('[data-mx-initial-group]');
    return gallery ? gallery.getAttribute('data-mx-initial-group') : null;
  }

  /** @param {PointerEvent} event */
  function cardSwatchLabel(event) {
    if (event.pointerType && event.pointerType !== 'mouse') return null;
    if (!(event.target instanceof Element)) return null;
    return event.target.closest(`${CARD_PICKER} label[data-media-id]`);
  }

  // Hover a card swatch: reveal that color's photos (the theme then scrolls to its first photo).
  document.addEventListener(
    'pointerover',
    (event) => {
      const label = cardSwatchLabel(event);
      if (label) showCardGroup(label.closest('product-card'), label.getAttribute('data-media-id'));
    },
    true
  );

  // Leave the swatch: go back to the selected color's photos.
  document.addEventListener(
    'pointerout',
    (event) => {
      const label = cardSwatchLabel(event);
      if (!label) return;
      if (event.relatedTarget instanceof Node && label.contains(event.relatedTarget)) return;

      const card = label.closest('product-card');
      if (card) showCardGroup(card, selectedCardGroup(card));
    },
    true
  );

  // When the theme finishes updating a card after a swatch click, make sure the right group is showing.
  document.addEventListener(
    'variant:update',
    (event) => {
      const picker = event.target;
      if (!(picker instanceof Element) || picker.tagName.toLowerCase() !== CARD_PICKER) return;

      const card = picker.closest('product-card');
      if (card) showCardGroup(card, selectedCardGroup(card));
    },
    true
  );

  /* ---------------- Product page ---------------- */

  const variantData = new Map();

  /** @param {string | undefined} productId */
  function variantsFor(productId) {
    if (!productId) return null;
    if (variantData.has(productId)) return variantData.get(productId);

    const el = document.querySelector(`script[data-mx-variants="${productId}"]`);
    if (!el) return null;

    try {
      const data = JSON.parse(el.textContent || '[]');
      variantData.set(productId, data);
      return data;
    } catch (error) {
      return null;
    }
  }

  /**
   * Runs before the theme handles a color change. If the shopper's current size is sold out
   * in the new color, quietly select the closest in-stock size so they see "Add to cart".
   * @param {HTMLInputElement} input - the color radio that was just chosen
   */
  function keepBuyableSize(input) {
    const picker = input.closest('variant-picker');
    if (!picker || !input.closest('.variant-option--swatches')) return;

    const variants = variantsFor(picker.dataset.productId);
    const changed = Number.parseInt(input.dataset.fieldsetIndex || '', 10);
    if (!variants || !variants.length || Number.isNaN(changed)) return;

    const optionCount = variants[0].o.length;
    /** @type {(string | null)[]} */
    const current = [];

    for (let i = 0; i < optionCount; i++) {
      if (i === changed) {
        current.push(input.value);
        continue;
      }
      const checked = picker.querySelector(`fieldset[data-fieldset-index="${i}"] input:checked`);
      current.push(checked instanceof HTMLInputElement ? checked.value : null);
    }

    const exact = variants.find((variant) =>
      variant.o.every((value, i) => current[i] === null || value === current[i])
    );
    if (exact && exact.a) return; // already in stock, nothing to do

    let best = null;
    let bestScore = -1;

    for (const variant of variants) {
      if (!variant.a || variant.o[changed] !== input.value) continue;

      let score = 0;
      variant.o.forEach((value, i) => {
        if (i !== changed && current[i] !== null && value === current[i]) score++;
      });

      if (score > bestScore) {
        best = variant;
        bestScore = score;
      }
    }

    if (!best) return; // every size of this color is sold out: leave it showing "Sold out"

    best.o.forEach((value, i) => {
      if (i === changed || current[i] === null || value === current[i]) return;

      const option = [...picker.querySelectorAll(`fieldset[data-fieldset-index="${i}"] input`)].find(
        (el) => el instanceof HTMLInputElement && el.value === value
      );
      if (!(option instanceof HTMLInputElement)) return;

      if (typeof picker.updateSelectedOption === 'function') {
        picker.updateSelectedOption(option);
      } else {
        option.checked = true;
      }
    });

    // The theme writes this id into the URL (?variant=), so point it at the in-stock variant.
    input.dataset.variantId = String(best.id);
  }

  /* ---------------- Change handler (runs before the theme's own handlers) ---------------- */

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

      // Card swatch: swap the card photo right away instead of waiting for the network round-trip.
      const cardPicker = input.closest(CARD_PICKER);
      if (cardPicker) {
        const card = cardPicker.closest('product-card');
        const mediaId = input.dataset.optionMediaId;

        if (card && mediaId) {
          showCardGroup(card, mediaId);
          if (typeof card.previewVariant === 'function') {
            try {
              card.previewVariant(mediaId);
            } catch (error) {
              /* the theme will select the photo once its update finishes */
            }
          }
        }
        return;
      }

      keepBuyableSize(input);
    },
    true
  );

  window.mxSwatches = { showCardGroup };
})();
